import { useState } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router'
import { Phone, Headphones, FlaskConical, Laptop } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { CallStagingHeader } from '../components/call-staging/CallStagingHeader'
import { CallStagingBriefing } from '../components/contact/CallStagingBriefing'
import { PhoneCallHistory } from '../components/phone/PhoneCallHistory'
import { useContactByPhone } from '../api/contacts'
import { useListScenarios } from '../api/copilotSandbox'
import { useMe } from '../api/auth'
import type { CopilotTransport } from '../api/copilot'
import { normalizePhone, toE164 } from '../lib/utils'
import { PhoneInput } from '../components/ui/PhoneInput'

export function CallStagingPage() {
  const { t } = useTranslation('calls')
  const { phoneNumber: rawParam } = useParams()
  const phoneNumber = rawParam ? decodeURIComponent(rawParam) : ''
  const [searchParams] = useSearchParams()
  const contactId = searchParams.get('contactId')
  const navigate = useNavigate()

  const explicitContactId = contactId ? Number(contactId) : undefined
  const { data: phoneContact } = useContactByPhone(
    explicitContactId ? undefined : phoneNumber,
  )
  const briefingContactId = explicitContactId ?? phoneContact?.id

  // Feature-flag probe: the sandbox route only exists when COPILOT_SANDBOX_ENABLED=true.
  // Show the entry only when the GET succeeds; hide on any error.
  // Must be called unconditionally (before any early return) per the Rules of Hooks.
  const { data: sandboxScenarios } = useListScenarios()
  const sandboxAvailable = Array.isArray(sandboxScenarios)

  // Per-call rep transport, seeded from the user's saved default
  // (Profile → "Default call method"); falls back to phone (PSTN). The user
  // can flip it per call below before starting the Co-pilot call.
  const { data: me } = useMe()
  const savedTransport: CopilotTransport =
    me?.user.copilot_call_transport === 'webrtc' ? 'webrtc' : 'pstn'
  const [transport, setTransport] = useState<CopilotTransport | null>(null)
  const effectiveTransport: CopilotTransport = transport ?? savedTransport

  if (!phoneNumber) {
    return <PhoneNumberInput onSubmit={(num) => navigate(`/app/calls/stage/${encodeURIComponent(num)}`)} />
  }

  const baseQuery = contactId ? `contactId=${contactId}` : ''
  const contactQuery = baseQuery ? `?${baseQuery}` : ''
  const startAgent = () => navigate(`/app/calls/agent/${encodeURIComponent(phoneNumber)}${contactQuery}`)
  // Carry the chosen transport onto the copilot route so CopilotCallPage knows
  // whether to dial the rep's phone (pstn) or mount the in-browser leg (webrtc).
  const copilotQuery = [baseQuery, `mode=${effectiveTransport}`]
    .filter(Boolean)
    .join('&')
  const startCopilot = () =>
    navigate(`/app/calls/copilot/${encodeURIComponent(phoneNumber)}?${copilotQuery}`)

  return (
    <div className="space-y-6">
      <CallStagingHeader
        phoneNumber={phoneNumber}
        contactId={contactId ? Number(contactId) : undefined}
      />

      {briefingContactId != null && (
        <CallStagingBriefing contactId={briefingContactId} />
      )}

      <div className="rounded-lg border border-border bg-surface-card p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-text-secondary">
          {t('staging.chooseCallType')}
        </h2>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={startAgent}
            className="flex flex-col items-start gap-2 rounded-lg border border-border bg-surface p-4 text-left transition-colors hover:border-primary hover:bg-primary/5"
          >
            <span className="inline-flex items-center gap-2 text-base font-semibold text-text-primary">
              <Phone className="h-5 w-5 text-primary" />
              {t('staging.startAgentCall')}
            </span>
            <span className="text-sm text-text-secondary">
              {t('staging.agentCallDescription')}
            </span>
          </button>
          <div className="flex flex-col items-start gap-3 rounded-lg border border-border bg-surface p-4 text-left">
            <span className="inline-flex items-center gap-2 text-base font-semibold text-text-primary">
              <Headphones className="h-5 w-5 text-primary" />
              {t('staging.startCopilotCall')}
            </span>
            <span className="text-sm text-text-secondary">
              {t('staging.copilotCallDescription')}
            </span>

            {/* Per-call transport toggle, seeded from the user's saved default.
                "On my phone" (pstn) dials the rep's real phone; "In browser"
                (webrtc) takes the call in this tab via the Twilio Voice SDK. */}
            <div className="w-full">
              <span className="block text-xs font-medium text-text-secondary">
                {t('staging.takeCallLabel')}
              </span>
              <div className="mt-1.5 inline-flex rounded-lg border border-border bg-surface-card p-0.5">
                <button
                  type="button"
                  onClick={() => setTransport('pstn')}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    effectiveTransport === 'pstn'
                      ? 'bg-primary text-white'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  <Phone className="h-3.5 w-3.5" />
                  {t('staging.takeCallOnPhone')}
                </button>
                <button
                  type="button"
                  onClick={() => setTransport('webrtc')}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    effectiveTransport === 'webrtc'
                      ? 'bg-primary text-white'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  <Laptop className="h-3.5 w-3.5" />
                  {t('staging.takeCallInBrowser')}
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={startCopilot}
              className="mt-1 inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
            >
              <Headphones className="h-4 w-4" />
              {t('staging.startCopilotCall')}
            </button>
          </div>
          {sandboxAvailable && (
            <button
              type="button"
              onClick={() => navigate('/app/calls/copilot/sandbox')}
              className="flex flex-col items-start gap-2 rounded-lg border border-dashed border-primary/50 bg-primary/5 p-4 text-left transition-colors hover:border-primary hover:bg-primary/10"
            >
              <span className="inline-flex items-center gap-2 text-base font-semibold text-text-primary">
                <FlaskConical className="h-5 w-5 text-primary" />
                Simulate (dev)
              </span>
              <span className="text-sm text-text-secondary">
                Replay a scripted scenario through the copilot analyzer — no real call needed.
              </span>
            </button>
          )}
        </div>
      </div>

      <PhoneCallHistory phoneNumber={phoneNumber} />
    </div>
  )
}

function PhoneNumberInput({ onSubmit }: { onSubmit: (phone: string) => void }) {
  const { t } = useTranslation('calls')
  const [phone, setPhone] = useState('')
  const complete = normalizePhone(phone).length === 10

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (complete) onSubmit(toE164(phone))
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="w-full max-w-md space-y-6 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Phone className="h-8 w-8 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-text-primary">{t('staging.stageACall')}</h1>
          <p className="mt-2 text-sm text-text-secondary">
            {t('staging.enterPhone')}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="flex gap-2">
          <PhoneInput
            value={phone}
            onChange={setPhone}
            placeholder={t('staging.phonePlaceholder')}
            className="flex-1 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            autoFocus
          />
          <button
            type="submit"
            disabled={!complete}
            className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {t('staging.stageCall')}
          </button>
        </form>
      </div>
    </div>
  )
}
